import { DialogProps, useMediaQuery, useTheme } from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import { MoovyButton, MoovyMutationError } from '..'

import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { UseMutationResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { MoovyPalette } from '../../styles/theme'

export interface MoovyDialogProps extends DialogProps {
  open: boolean
  mutation?: UseMutationResult<AxiosResponse<any, any>, unknown, any, unknown>
  errorTitleKey?: string
  children: ReactNode
  modalTitle: any
  submitButtonContent: ReactNode | string
  formReference?: string
  leftSideButtons?: [ReactNode]
  contentPadding?: number
  hideCancelButton?: boolean
  deleteButtonContent?: ReactNode | string
  onDelete?: (event: any) => void
  onCancel: () => void
  onSubmit?: (event: any) => void
  'data-testid'?: string
}

const MoovyDialog = (props: MoovyDialogProps) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const {
    open,
    mutation,
    errorTitleKey,
    children,
    modalTitle,
    submitButtonContent,
    formReference,
    leftSideButtons,
    deleteButtonContent,
    contentPadding,
    hideCancelButton,
    onCancel,
    onSubmit,
    onDelete,
    'data-testid': dataTestId,
    ...other
  } = props

  const DefaultSubmitButton = (props: { hidden?: boolean }) => {
    return (
      <MoovyButton
        type="submit"
        form={formReference}
        mutation={mutation}
        sx={{ display: props.hidden ? 'none' : 'block' }}
      >
        {submitButtonContent}
      </MoovyButton>
    )
  }
  return (
    <>
      <Dialog
        open={open}
        onSubmit={onSubmit}
        fullScreen={fullScreen}
        onClose={onCancel}
        sx={
          fullScreen
            ? undefined
            : {
                '& .MuiDialog-paper': {
                  // Minimum width of all Moovy dialogs is set here
                  minWidth: '350px',
                },
              }
        }
        data-testid={dataTestId}
        {...other}
      >
        <DialogTitle sx={{ pr: 7 }}>{modalTitle}</DialogTitle>
        <IconButton
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 10,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <DialogContent
          dividers
          sx={{ padding: contentPadding, position: 'relative' }}
        >
          {mutation && (
            <div
              style={{
                marginBottom: mutation?.error ? '1rem' : undefined,
              }}
            >
              <MoovyMutationError
                mutation={mutation}
                errorTitleKey={errorTitleKey || 'common.actions.error'}
              />
            </div>
          )}
          {children}
          <Box sx={{ opacity: 0.5 }}>
            <Backdrop
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: MoovyPalette.colorMainBackground,
                zIndex: 1,
              }}
              open={mutation?.isPending || false}
            >
              <CircularProgress />
            </Backdrop>
          </Box>
        </DialogContent>
        {/* Hidden button first in DOM to determine the default action when the form is submitted with Enter key */}
        <DefaultSubmitButton hidden />
        <DialogActions sx={{ padding: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                leftSideButtons || deleteButtonContent
                  ? 'space-between'
                  : 'right',
              width: '100%',
            }}
          >
            {[
              ...(leftSideButtons?.map((item) => item) || []),
              (props.deleteButtonContent && (
                <MoovyButton
                  color={'error'}
                  onClick={props.onDelete}
                  type="submit"
                  formAction="delete"
                  form={formReference}
                >
                  {props.deleteButtonContent}
                </MoovyButton>
              )) ||
                null,
            ]}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!hideCancelButton && (
                <MoovyButton
                  onClick={() => {
                    onCancel()
                  }}
                  variant="text"
                >
                  <FormattedMessage id="common.buttons.cancel" />
                </MoovyButton>
              )}
              <DefaultSubmitButton />
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MoovyDialog
